import { Vec3 } from 'playcanvas';

import { Events } from './events';
import { withProvenance } from './provenance';

type Bookmark = {
    id: string;
    name: string;
    visible?: boolean;
    visibilityTouched?: boolean;
    cameraPose: {
        position: [number, number, number];
        target: [number, number, number];
        fov?: number;
    };
    visibleLayerIds?: string[];
    controlMode?: 'orbit' | 'fly' | 'walk';
    note?: string;
    author?: string;
    createdAt: string;
};

const isFiniteNumber = (value: unknown): value is number => {
    return typeof value === 'number' && Number.isFinite(value);
};

const sanitizeVec3Tuple = (value: unknown): [number, number, number] | null => {
    if (!Array.isArray(value) || value.length !== 3) {
        return null;
    }

    const [x, y, z] = value;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

const sanitizeVec3TupleOrObject = (value: unknown): [number, number, number] | null => {
    const tuple = sanitizeVec3Tuple(value);
    if (tuple) {
        return tuple;
    }

    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    const x = candidate.x;
    const y = candidate.y;
    const z = candidate.z;
    if (!isFiniteNumber(x) || !isFiniteNumber(y) || !isFiniteNumber(z)) {
        return null;
    }

    return [x, y, z];
};

const sanitizeControlMode = (value: unknown): Bookmark['controlMode'] | undefined => {
    return value === 'orbit' || value === 'fly' || value === 'walk' ? value : undefined;
};

const sanitizeVisibleLayerIds = (value: unknown): string[] | undefined => {
    if (!Array.isArray(value)) {
        return undefined;
    }

    const filtered = value.filter((item): item is string => typeof item === 'string');
    return filtered.length > 0 ? filtered : undefined;
};

const sanitizeBookmark = (value: unknown): Bookmark | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const candidate = value as Record<string, unknown>;
    if (typeof candidate.id !== 'string' || candidate.id.length === 0) {
        return null;
    }

    const name = typeof candidate.name === 'string' ? candidate.name :
        typeof candidate.title === 'string' ? candidate.title :
            typeof candidate.label === 'string' ? candidate.label :
                null;
    if (!name) {
        return null;
    }

    const createdAt = typeof candidate.createdAt === 'string' && candidate.createdAt.length > 0 ? candidate.createdAt :
        new Date().toISOString();

    if (!candidate.cameraPose || typeof candidate.cameraPose !== 'object') {
        return null;
    }

    const cameraPose = candidate.cameraPose as Record<string, unknown>;
    const position = sanitizeVec3TupleOrObject(cameraPose.position);
    const target = sanitizeVec3TupleOrObject(cameraPose.target);

    if (!position || !target) {
        return null;
    }

    const sanitized: Bookmark = {
        id: candidate.id,
        name,
        cameraPose: {
            position,
            target
        },
        createdAt
    };

    sanitized.visible = candidate.visible === undefined ? true : !!candidate.visible;
    sanitized.visibilityTouched = !!candidate.visibilityTouched;

    if (isFiniteNumber(cameraPose.fov)) {
        sanitized.cameraPose.fov = cameraPose.fov;
    }

    const visibleLayerIds = sanitizeVisibleLayerIds(candidate.visibleLayerIds);
    if (visibleLayerIds) {
        sanitized.visibleLayerIds = visibleLayerIds;
    }

    const controlMode = sanitizeControlMode(candidate.controlMode);
    if (controlMode) {
        sanitized.controlMode = controlMode;
    }

    if (typeof candidate.note === 'string') {
        sanitized.note = candidate.note;
    }

    if (typeof candidate.author === 'string') {
        sanitized.author = candidate.author;
    }

    return sanitized;
};

const sanitizeBookmarkPatch = (value: unknown): Partial<Bookmark> | null => {
    if (!value || typeof value !== 'object') {
        return null;
    }

    const patch = value as Record<string, unknown>;
    const sanitized: Partial<Bookmark> = {};

    if (typeof patch.name === 'string') {
        sanitized.name = patch.name;
    }

    if ('visible' in patch && (patch.visible === undefined || typeof patch.visible === 'boolean')) {
        sanitized.visible = patch.visible as boolean | undefined;
        sanitized.visibilityTouched = true;
    }

    if (typeof patch.createdAt === 'string') {
        sanitized.createdAt = patch.createdAt;
    }

    if (typeof patch.note === 'string') {
        sanitized.note = patch.note;
    }

    if (typeof patch.author === 'string') {
        sanitized.author = patch.author;
    }

    if (Array.isArray(patch.visibleLayerIds)) {
        sanitized.visibleLayerIds = sanitizeVisibleLayerIds(patch.visibleLayerIds);
    }

    const controlMode = sanitizeControlMode(patch.controlMode);
    if (controlMode) {
        sanitized.controlMode = controlMode;
    }

    if (patch.cameraPose && typeof patch.cameraPose === 'object') {
        const cameraPose = patch.cameraPose as Record<string, unknown>;
        const sanitizedCameraPose: Partial<Bookmark['cameraPose']> = {};

        const position = sanitizeVec3TupleOrObject(cameraPose.position);
        if (position) {
            sanitizedCameraPose.position = position;
        }

        const target = sanitizeVec3TupleOrObject(cameraPose.target);
        if (target) {
            sanitizedCameraPose.target = target;
        }

        if ('fov' in cameraPose && (cameraPose.fov === undefined || isFiniteNumber(cameraPose.fov))) {
            sanitizedCameraPose.fov = cameraPose.fov as number | undefined;
        }

        if (Object.keys(sanitizedCameraPose).length > 0) {
            sanitized.cameraPose = sanitizedCameraPose as Bookmark['cameraPose'];
        }
    }

    return sanitized;
};

const cloneBookmark = (bookmark: Bookmark): Bookmark => {
    return {
        ...bookmark,
        cameraPose: {
            ...bookmark.cameraPose,
            position: [bookmark.cameraPose.position[0], bookmark.cameraPose.position[1], bookmark.cameraPose.position[2]],
            target: [bookmark.cameraPose.target[0], bookmark.cameraPose.target[1], bookmark.cameraPose.target[2]]
        },
        visibleLayerIds: bookmark.visibleLayerIds?.slice()
    };
};

const registerBookmarksEvents = (events: Events) => {
    let bookmarks: Bookmark[] = [];
    let selectedId: string | null = null;

    const listBookmarks = () => bookmarks.map(cloneBookmark);

    const goToBookmark = (id: string) => {
        if (typeof id !== 'string') {
            return false;
        }

        const bookmark = bookmarks.find(item => item.id === id);
        if (!bookmark) {
            return false;
        }

        const position = sanitizeVec3Tuple(bookmark.cameraPose?.position);
        const target = sanitizeVec3Tuple(bookmark.cameraPose?.target);
        if (!position || !target) {
            return false;
        }

        try {
            events.fire('camera.setPose', {
                position: new Vec3(position),
                target: new Vec3(target)
            }, 1);

            if (isFiniteNumber(bookmark.cameraPose.fov)) {
                events.fire('camera.setFov', bookmark.cameraPose.fov);
            }
        } catch (error) {
            return false;
        }

        return true;
    };

    const fireListChanged = () => {
        events.fire('bookmarks.listChanged', listBookmarks());
    };

    const fireSelectedChanged = () => {
        events.fire('bookmarks.selectedChanged', selectedId);
    };

    const setSelected = (id: string | null) => {
        const next = typeof id === 'string' && id.length > 0 ? id : null;
        if (next === selectedId) {
            return;
        }
        selectedId = next;
        fireSelectedChanged();
    };

    events.function('bookmarks.list', () => listBookmarks());
    events.function('bookmarks.goTo', (id: string) => goToBookmark(id));
    events.function('bookmarks.selected', () => selectedId);

    events.on('bookmarks.select', (id: unknown) => {
        if (id === null || id === undefined || id === '') {
            setSelected(null);
            return;
        }
        if (typeof id !== 'string') {
            return;
        }
        if (!bookmarks.some(item => item.id === id)) {
            return;
        }
        setSelected(id);
    });

    events.on('bookmarks.add', (bookmark: unknown) => {
        if (!bookmark || typeof bookmark !== 'object') {
            return;
        }

        const stamped = withProvenance(events, bookmark as { author?: string; createdAt?: string });
        const sanitized = sanitizeBookmark(stamped);
        if (!sanitized) {
            return;
        }

        if (sanitized.visible === undefined) {
            sanitized.visible = true;
        }
        if (sanitized.visibilityTouched === undefined) {
            sanitized.visibilityTouched = false;
        }

        if (bookmarks.some(existing => existing.id === sanitized.id)) {
            return;
        }
        bookmarks = bookmarks.concat(cloneBookmark(sanitized));
        fireListChanged();
    });

    events.on('bookmarks.setVisible', (payload: { id: string; visible: boolean }) => {
        const id = payload?.id;
        if (typeof id !== 'string' || id.length === 0) {
            return;
        }

        let changed = false;
        bookmarks = bookmarks.map((bookmark) => {
            if (bookmark.id !== id) {
                return bookmark;
            }

            changed = true;
            return {
                ...bookmark,
                visible: !!payload.visible,
                visibilityTouched: true
            };
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('bookmarks.applyVisibility', (visibleIds: unknown) => {
        if (!Array.isArray(visibleIds)) {
            return;
        }

        const visibleSet = new Set(visibleIds.filter((id): id is string => typeof id === 'string' && id.length > 0));

        let changed = false;
        bookmarks = bookmarks.map((bookmark) => {
            const nextVisible = visibleSet.has(bookmark.id);
            if (bookmark.visible === nextVisible && bookmark.visibilityTouched) {
                return bookmark;
            }
            changed = true;
            return {
                ...bookmark,
                visible: nextVisible,
                visibilityTouched: true
            };
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('bookmarks.update', (payload: { id: string; patch: Partial<Bookmark> }) => {
        const id = payload?.id;
        const patch = payload?.patch;

        if (typeof id !== 'string') {
            return;
        }

        const sanitizedPatch = sanitizeBookmarkPatch(patch);
        if (!sanitizedPatch) {
            return;
        }

        let changed = false;
        bookmarks = bookmarks.map((bookmark) => {
            if (bookmark.id !== id) {
                return bookmark;
            }

            const nextCameraPose = sanitizedPatch.cameraPose ? {
                ...bookmark.cameraPose,
                ...sanitizedPatch.cameraPose,
                position: sanitizedPatch.cameraPose.position ?? bookmark.cameraPose.position,
                target: sanitizedPatch.cameraPose.target ?? bookmark.cameraPose.target
            } : bookmark.cameraPose;

            const merged = {
                ...bookmark,
                ...sanitizedPatch,
                id: bookmark.id,
                cameraPose: nextCameraPose
            } as Bookmark;

            const sanitizedMerged = sanitizeBookmark(merged);
            if (!sanitizedMerged) {
                return bookmark;
            }

            changed = true;
            return cloneBookmark(sanitizedMerged);
        });

        if (changed) {
            fireListChanged();
        }
    });

    events.on('bookmarks.remove', (id: string) => {
        if (typeof id !== 'string') {
            return;
        }
        const next = bookmarks.filter(bookmark => bookmark.id !== id);
        if (next.length === bookmarks.length) {
            return;
        }
        bookmarks = next;
        if (selectedId === id) {
            selectedId = null;
            fireSelectedChanged();
        }
        fireListChanged();
    });

    events.on('bookmarks.reorder', (orderedIds: string[]) => {
        if (!Array.isArray(orderedIds)) {
            return;
        }

        const idSet = new Set(orderedIds);
        const byId = new Map(bookmarks.map(bookmark => [bookmark.id, bookmark]));

        const ordered = orderedIds
        .map(id => byId.get(id))
        .filter((bookmark): bookmark is Bookmark => !!bookmark);

        const remainder = bookmarks.filter(bookmark => !idSet.has(bookmark.id));
        const next = ordered.concat(remainder);

        if (next.length !== bookmarks.length) {
            return;
        }

        const changed = next.some((bookmark, index) => bookmark.id !== bookmarks[index]?.id);
        if (!changed) {
            return;
        }

        bookmarks = next;
        fireListChanged();
    });

    events.on('bookmarks.goTo', (id: string) => {
        goToBookmark(id);
    });

    events.on('bookmarks.setAll', (next: Bookmark[] | undefined) => {
        if (!Array.isArray(next)) {
            bookmarks = [];
            if (selectedId !== null) {
                selectedId = null;
                fireSelectedChanged();
            }
            fireListChanged();
            return;
        }

        const seenIds = new Set<string>();
        const sanitized: Bookmark[] = [];

        for (const item of next) {
            const bookmark = sanitizeBookmark(item);
            if (!bookmark || seenIds.has(bookmark.id)) {
                continue;
            }

            seenIds.add(bookmark.id);
            sanitized.push(cloneBookmark(bookmark));
        }

        bookmarks = sanitized;
        if (selectedId && !bookmarks.some(item => item.id === selectedId)) {
            selectedId = null;
            fireSelectedChanged();
        }
        fireListChanged();
    });

    events.on('scene.clear', () => {
        bookmarks = [];
        if (selectedId !== null) {
            selectedId = null;
            fireSelectedChanged();
        }
        fireListChanged();
    });

    fireListChanged();
    fireSelectedChanged();
};

export { registerBookmarksEvents };
export type { Bookmark };
